import { Controller } from "@hotwired/stimulus"
import { buildComparisonCard } from "../modules/money_smart/comparison_helpers"

const MAX_COMPARE = 3

export default class extends Controller {
  static targets = [
    "tableBody", "itemCount", "noResults",
    "compareButton", "compareCount", "comparisonPanel", "comparisonCards",
    "sortSelect", "filter"
  ]

  static values = {
    fieldConfig: { type: Array, default: [] },
    countLabel: { type: String, default: "items" }
  }

  selectedIds = new Set()

  connect() { this.restoreFromUrl() }

  filter() {
    let visible = 0

    this.rows.forEach(row => {
      const match = this.filterTargets.every(select => {
        const key = select.dataset.filterKey
        return select.value === "all" || row.dataset[key] === select.value
      })

      row.classList.toggle("hidden", !match)
      if (match) visible++
    })

    this.itemCountTarget.textContent = visible
    this.noResultsTarget.classList.toggle("hidden", visible > 0)
    this.updateUrl()
  }

  sort() {
    const val = this.sortSelectTarget.value
    const [key, direction] = val.split("-")
    const tbody = this.tableBodyTarget
    const rows = [...this.rows]

    rows.sort((a, b) => {
      const aVal = this.sortValue(a, key)
      const bVal = this.sortValue(b, key)

      if (typeof aVal === "string") {
        return direction === "asc" ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal)
      }
      if (isNaN(aVal) && isNaN(bVal)) return 0
      if (isNaN(aVal)) return 1
      if (isNaN(bVal)) return -1
      return direction === "asc" ? aVal - bVal : bVal - aVal
    })

    rows.forEach(row => tbody.appendChild(row))
    this.updateUrl()
  }

  toggleCompare(event) {
    const cb = event.target
    const id = cb.dataset.itemId

    if (cb.checked) {
      if (this.selectedIds.size >= MAX_COMPARE) { cb.checked = false; return }
      this.selectedIds.add(id)
    } else {
      this.selectedIds.delete(id)
    }

    this.compareCountTarget.textContent = this.selectedIds.size
    this.compareButtonTarget.disabled = this.selectedIds.size < 2
    this.updateUrl()
  }

  showComparison() {
    if (this.selectedIds.size < 2) return

    const cards = []
    this.selectedIds.forEach(id => {
      const row = this.tableBodyTarget.querySelector(`tr[data-item-id="${id}"]`)
      if (row) cards.push(buildComparisonCard(row, this.fieldConfigValue))
    })

    this.comparisonCardsTarget.innerHTML = cards.join("")
    this.comparisonPanelTarget.classList.remove("hidden")
    this.comparisonPanelTarget.scrollIntoView({ behavior: "smooth", block: "start" })
  }

  hideComparison() { this.comparisonPanelTarget.classList.add("hidden") }

  clearFilters() {
    this.filterTargets.forEach(s => { s.value = "all" })
    this.filter()
  }

  updateUrl() {
    const params = new URLSearchParams()

    this.filterTargets.forEach(select => {
      if (select.value !== "all") params.set(select.dataset.filterKey, select.value)
    })

    if (this.sortSelectTarget.value !== this.sortSelectTarget.options[0]?.value) {
      params.set("sort", this.sortSelectTarget.value)
    }
    if (this.selectedIds.size > 0) params.set("compare", [...this.selectedIds].join(","))

    const url = params.toString()
      ? `${window.location.pathname}?${params}`
      : window.location.pathname
    window.history.replaceState(null, "", url)
  }

  restoreFromUrl() {
    const params = new URLSearchParams(window.location.search)
    let hasFilters = false

    this.filterTargets.forEach(select => {
      const key = select.dataset.filterKey
      if (params.has(key)) { select.value = params.get(key); hasFilters = true }
    })

    if (params.has("sort")) this.sortSelectTarget.value = params.get("sort")

    if (params.has("compare")) {
      params.get("compare").split(",").forEach(id => {
        if (this.selectedIds.size < MAX_COMPARE) {
          const cb = this.tableBodyTarget.querySelector(`input[data-item-id="${id}"]`)
          if (cb) { cb.checked = true; this.selectedIds.add(id) }
        }
      })
      this.compareCountTarget.textContent = this.selectedIds.size
      this.compareButtonTarget.disabled = this.selectedIds.size < 2
    }

    if (hasFilters) this.filter()
    if (params.has("sort")) this.sort()
  }

  sortValue(row, key) {
    const val = row.dataset[key]
    if (key === "provider" || key === "name") return (val || "").toLowerCase()
    return parseFloat(val)
  }

  get rows() { return this.tableBodyTarget.querySelectorAll("tr[data-item-id]") }
}
